import { useNotificationText } from './useNotificationText';
import { formatRelativeTime } from '@/utils/format';
import { useNotifications } from '../../contexts/NotificationContext';
import type {
  Notification,
  NotificationType,
} from '../../types/notification.types';
import {
  CheckCircle,
  XCircle,
  Calendar,
  Bell,
  DollarSign,
  AlertCircle,
  BellRing,
  Mail,
  Clock,
  UserX,
  Package,
  MessageSquare,
  AtSign,
  ClipboardList,
  Repeat,
  ScanLine,
  CalendarX,
  CalendarPlus,
  CalendarClock,
  Undo2,
  UserCheck,
  Wallet,
  FileSignature,
  FileX,
  Coins,
} from 'lucide-react';

interface NotificationItemProps {
  notification: Notification;
  onClose: () => void;
}

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'appointment_request':
      return <Calendar className="w-5 h-5 text-info-500" />;
    case 'appointment_request_expiring':
    case 'appointment_request_expired':
    case 'pending_appointment_approval':
      return <Clock className="w-5 h-5 text-warning-500" />;
    case 'appointment_approved':
      return <CheckCircle className="w-5 h-5 text-success-500" />;
    case 'appointment_rejected':
      return <XCircle className="w-5 h-5 text-danger-500" />;
    case 'appointment_reminder':
      return <Bell className="w-5 h-5 text-warning-500" />;
    case 'reservation_completed':
      return <CheckCircle className="w-5 h-5 text-success-500" />;
    case 'no_show_summary':
      return <UserX className="w-5 h-5 text-danger-500" />;
    case 'payment_received':
    case 'payment_pending':
      return <DollarSign className="w-5 h-5 text-success-500" />;
    case 'patient_called':
      return <BellRing className="w-5 h-5 text-orange-500 animate-pulse" />;
    case 'invitation_received':
    case 'invitation_accepted':
    case 'invitation_declined':
      return <Mail className="w-5 h-5 text-primary-500" />;
    case 'chat_message':
      return <MessageSquare className="w-5 h-5 text-info-500" />;
    case 'mention':
      return <AtSign className="w-5 h-5 text-warning-500" />;
    case 'low_stock':
    case 'critical_stock':
    case 'out_of_stock':
      return <Package className="w-5 h-5 text-warning-500" />;
    case 'lab_delivery_late':
      return <AlertCircle className="w-5 h-5 text-danger-500" />;
    case 'lab_work_arrived':
      return <Package className="w-5 h-5 text-success-500" />;
    case 'xray_uploaded':
      return <ScanLine className="w-5 h-5 text-primary-500" />;
    case 'stock_transfer':
      return <Package className="w-5 h-5 text-primary-500" />;
    case 'waitlist_slot':
      return <ClipboardList className="w-5 h-5 text-primary-500" />;
    case 'recurring_expense_generated':
      return <Repeat className="w-5 h-5 text-primary-500" />;
    case 'plan_signed_remotely':
    case 'medical_form_submitted':
      return <CheckCircle className="w-5 h-5 text-success-500" />;
    case 'plan_instalments_overdue':
      return <AlertCircle className="w-5 h-5 text-danger-500" />;
    case 'appointment_cancelled':
      return <CalendarX className="w-5 h-5 text-danger-500" />;
    case 'appointment_booked':
      return <CalendarPlus className="w-5 h-5 text-info-500" />;
    case 'appointment_rescheduled':
      return <CalendarClock className="w-5 h-5 text-warning-500" />;
    case 'patient_checked_in':
      return <UserCheck className="w-5 h-5 text-success-500" />;
    case 'refund_issued':
      return <Undo2 className="w-5 h-5 text-warning-500" />;
    case 'payroll_review':
      return <Wallet className="w-5 h-5 text-warning-500" />;
    case 'payroll_approved':
      return <Wallet className="w-5 h-5 text-success-500" />;
    case 'payroll_returned':
      return <Wallet className="w-5 h-5 text-danger-500" />;
    case 'consent_signed_remotely':
      return <FileSignature className="w-5 h-5 text-success-500" />;
    case 'invoice_voided':
      return <FileX className="w-5 h-5 text-danger-500" />;
    case 'cash_count_variance':
      return <Coins className="w-5 h-5 text-warning-500" />;
    default:
      return <AlertCircle className="w-5 h-5 text-neutral-500" />;
  }
};

export const NotificationItem = ({
  notification,
  onClose,
}: NotificationItemProps) => {
  const { openNotification } = useNotifications();
  const text = useNotificationText()(notification);

  const handleClick = () => {
    onClose();
    openNotification(notification);
  };

  return (
    <div
      onClick={handleClick}
      className={`px-4 py-3 cursor-pointer hover:bg-neutral-50 transition-colors ${
        !notification.isRead ? 'bg-info-50' : ''
      }`}
    >
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-1">
          {getNotificationIcon(notification.type)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-neutral-800">{text.title}</p>
          <p className="text-sm text-neutral-600 mt-1">{text.message}</p>
          <p className="text-xs text-neutral-400 mt-1">
            {formatRelativeTime(notification.createdAt)}
          </p>
        </div>
        {!notification.isRead && (
          <div className="flex-shrink-0">
            <div className="w-2 h-2 bg-info-600 rounded-full" />
          </div>
        )}
      </div>
    </div>
  );
};
